import React, { useMemo, useContext, SyntheticEvent, ReactText } from "react";
import { useTranslation } from "react-i18next";

import { FilterIcon } from "fe-shared/src/components/Icons";
import { MultiSelectInput } from "fe-shared/src/components";
import UserContext from "contexts/userContext";

import { DataGridFilters } from "fe-shared/src/components/DataGrid/modules/types";

import styles from "../InventoryAdjustmentsDataGrid.module.scss";

interface Props {
  filters: DataGridFilters;
  updateFilters: (filters: DataGridFilters) => void;
  supportedFilters?: FilterName[];
}

export enum FilterName {
  ShowAll = "ShowAll",
  Mine = "Mine",
}

const InventoryAdjustmentAdditionalFilters = (props: Props) => {
  const { filters, updateFilters, supportedFilters } = props;
  const { assigneeDisplayName } = filters;
  const { t } = useTranslation();
  const user = useContext(UserContext);
  const userName = `${user?.firstName} ${user?.lastName}`;
  const mineChecked = assigneeDisplayName?.value === userName;
  const showAllChecked = !mineChecked;
  const filtersCount = [mineChecked].filter(Boolean).length;

  const value = useMemo(
    () =>
      [
        {
          value: FilterName.ShowAll,
          isChecked: showAllChecked,
        },
        {
          value: FilterName.Mine,
          isChecked: mineChecked,
        },
      ]
        .filter((o) => o.isChecked)
        .map((o) => o.value),
    [mineChecked, showAllChecked]
  );

  const options = useMemo(
    () =>
      Object.values(FilterName)
        .filter((name) =>
          supportedFilters ? supportedFilters.includes(name) : true
        )
        .map((name) => ({
          value: name,
          label: t(`transactions.grid.additionalFilters.${name}`),
        })),
    [supportedFilters, t]
  );

  const placeholder = (
    <>
      <FilterIcon fillColor={null} />
      Filters
      {filtersCount ? ` +${filtersCount}` : ""}
    </>
  );

  const onChange = (
    e: SyntheticEvent,
    values: ReactText[],
    selectedValue: ReactText
  ) => {
    switch (selectedValue) {
      case FilterName.Mine:
        updateFilters({
          assigneeDisplayName: {
            type: "textFilter",
            value: mineChecked ? "" : userName,
          },
        });
        break;
      case FilterName.ShowAll:
        if (!value.includes(FilterName.ShowAll)) {
          updateFilters({
            assigneeDisplayName: {
              type: "textFilter",
              value: "",
            },
          });
        }
        break;
      default:
    }
  };

  return (
    <MultiSelectInput
      value={value}
      options={options}
      id="additional-filters-multi-select-input"
      onChange={onChange}
      placeholder={placeholder}
      className={styles.additionalFiltersSelect}
    />
  );
};

export default InventoryAdjustmentAdditionalFilters;
